import { gql, TypedDocumentNode, useSuspenseQuery } from '@apollo/client';
import { maxMeasure, minMeasure } from '@e2m-customer-portal/shared';
import { getMonths } from '../../utils';
import { NULLABLE_MEASURE_FIELDS } from '../../queries';
import {
  DefaultForecast,
  DefaultForecastLimitsQuery,
  DefaultForecastLimitsQueryVariables,
} from '../../generated/graphql';

export type DefaultForecastLimit = Pick<DefaultForecast, 'minimum' | 'maximum' | 'auxiliaryConsumption'>;

export function reduceForecastTemplateLimits(
  forecast: readonly Pick<DefaultForecast, 'month' | 'minimum' | 'maximum' | 'auxiliaryConsumption'>[],
  dateInput: Parameters<typeof getMonths>[0],
): DefaultForecastLimit {
  const months = getMonths(dateInput);
  return forecast
    .filter((limit) => months.includes(limit.month))
    .reduce<DefaultForecastLimit>(
      (a, b) => ({
        minimum: maxMeasure(a.minimum, b.minimum),
        maximum: minMeasure(a.maximum, b.maximum),
        auxiliaryConsumption: maxMeasure(a.auxiliaryConsumption, b.auxiliaryConsumption),
      }),
      {
        minimum: { value: null, unit: 'W' },
        maximum: { value: null, unit: 'W' },
        auxiliaryConsumption: { value: null, unit: 'W' },
      },
    );
}

export const forecastTemplateLimitsQuery: TypedDocumentNode<
  DefaultForecastLimitsQuery,
  DefaultForecastLimitsQueryVariables
> = gql`
  query DefaultForecastLimits($deviceId: ID!) {
    getDefaultForecast(deviceId: $deviceId) {
      id
      month
      minimum {
        ...NullableMeasureFields
      }
      maximum {
        ...NullableMeasureFields
      }
      auxiliaryConsumption {
        ...NullableMeasureFields
      }
    }
  }
  ${NULLABLE_MEASURE_FIELDS}
`;

export function useForecastTemplateLimits(
  deviceId: string,
  dateInput: Parameters<typeof getMonths>[0],
): DefaultForecastLimit {
  const months = getMonths(dateInput);
  if (months.length === 0) {
    throw new TypeError('At least one month must be selected.');
  }

  const { data } = useSuspenseQuery(forecastTemplateLimitsQuery, { variables: { deviceId } });

  return reduceForecastTemplateLimits(data.getDefaultForecast, dateInput);
}
